import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
	FILTER_LOCATION_KEY,
	FILTER_WORK_CENTERS_KEY,
} from "../../../../../../assets/constants";
import {
	LOCATIONS,
	USER_ROLES,
	USER_STATUS,
	WORK_CENTERS,
} from "../../../../../filters/constants";
import { Filter } from "../../../../../filters/filter-accordion/types";
import { DataRelation, FilterContext } from "../../../../../filters/filter-context";
import useGatAdminConfigData from "../../../../controllers/use-gat-admin-config-data";
import { HR_MANAGER } from "../../constant";
import { getUserRoleLabel } from "../../role-labels";
import useSupervisorsData from "./use-supervisors-data";

// Normalise a raw list to clean { id, name } options and drop any entry
// missing an id or name — the shared FilterPopover sorts options by name and
// would throw on a null/undefined name.
const toOptions = (items: Array<{ id: any; name: any }> | undefined) =>
	(items ?? [])
		.filter((item) => item?.id != null && item?.name)
		.map((item) => ({ id: item.id, name: String(item.name) }));

// Registers the Users table filters (Location, Work Center, User Roles,
// Status) into the FilterProvider, mirroring how the rest of the app builds
// filters. Options come from the company config master lists this page already
// loads (locations, work centers, roles). Must be rendered inside a
// FilterProvider.
const useUsersFilters = () => {
	const { t } = useTranslation();
	const { allCompanyUserRoles, getLocationsStatus, allCompanyWorkCenters, locations } =
		useGatAdminConfigData();
	const { companyUsers } = useSupervisorsData();

	const filterContext = useContext(FilterContext);
	if (!filterContext) {
		throw new Error("useUsersFilters must be used within a FilterProvider");
	}
	const { setFilters, setDataRelations } = filterContext;

	useEffect(() => {
		if (getLocationsStatus !== "fulfilled") return;

		const statusOptions = [
			{ id: 0, name: t("users_active") },
			{ id: 1, name: t("users_inactive") },
		];

		const roleOptions = toOptions(
			(allCompanyUserRoles ?? []).map((role: { id: any; name: any }) => ({
				id: role.id,
				name: getUserRoleLabel(role.name, t),
			}))
		);

		// The HR Manager role is not part of the admin roles list; pull its id
		// from the company users so it can still be filtered on.
		const hrManagerUser = companyUsers?.find((user: { roles: any[] }) =>
			user.roles?.find((role) => role.name === HR_MANAGER)
		);
		const hrManagerRoleId = hrManagerUser?.roles?.find(
			(role: { name: string }) => role.name === HR_MANAGER
		)?.id;
		if (hrManagerRoleId != null) {
			roleOptions.push({ id: hrManagerRoleId, name: getUserRoleLabel(HR_MANAGER, t) });
		}

		const locationOptions = toOptions(locations);
		const workCenterOptions = toOptions(allCompanyWorkCenters);

		const newFilters: Filter[] = [];
		if (locationOptions.length > 0) {
			newFilters.push({
				id: 1,
				name: LOCATIONS,
				key: FILTER_LOCATION_KEY,
				data: locationOptions,
				reducedData: locationOptions,
				searchedData: locationOptions,
			});
		}
		if (workCenterOptions.length > 0) {
			newFilters.push({
				id: 2,
				name: WORK_CENTERS,
				key: FILTER_WORK_CENTERS_KEY,
				data: workCenterOptions,
				reducedData: workCenterOptions,
				searchedData: workCenterOptions,
			});
		}
		if (roleOptions.length > 0) {
			newFilters.push({
				id: 3,
				name: USER_ROLES,
				data: roleOptions,
				reducedData: roleOptions,
				searchedData: roleOptions,
			});
		}
		newFilters.push({
			id: 4,
			name: USER_STATUS,
			data: statusOptions,
			reducedData: statusOptions,
			searchedData: statusOptions,
		});

		setFilters(newFilters);

		// Location → Work Center cascade: each location carries its work centers,
		// so picking a location narrows the Work Center options to that location's.
		const mappings = (locations ?? [])
			.filter((location: any) => location?.id != null)
			.map((location: any) => ({
				fromId: location.id,
				toIds: (location.workCenters ?? []).map((wc: { id: number }) => wc.id),
			}));
		const relations: DataRelation[] = [
			{
				from: FILTER_LOCATION_KEY,
				to: FILTER_WORK_CENTERS_KEY,
				mappings,
			},
		];
		setDataRelations(relations);
	}, [
		allCompanyUserRoles,
		getLocationsStatus,
		allCompanyWorkCenters,
		locations,
		companyUsers,
		setFilters,
		setDataRelations,
		t,
	]);
};

export default useUsersFilters;
